import MiniSearch from "minisearch";
import { useEffect, useState } from "react";
import { USER_FILES_UPDATED_EVENT } from "../constants/events";
import { useAuth } from "../context/AuthContext";
import { apiGet } from "../utils/apiClient";
import type { CatalogDocument } from "./useDocumentMention";

export type CatalogIndexStatus =
	| "idle"
	| "loading"
	| "ready"
	| "oversize"
	| "error";

type CatalogIndex = MiniSearch<CatalogDocument>;

type CatalogIndexStoreState = {
	status: CatalogIndexStatus;
	docs: CatalogDocument[];
	index: CatalogIndex | null;
	version: number;
};

// Keep in sync with BULK_CAP in backend/app/modules/agentic/shared/catalog_utils.py.
const BULK_CAP = 2000;
// Visible dropdown deal cap. Keep in sync with MAX_DEAL_RESULTS in backend/app/modules/agentic/shared/catalog_utils.py.
const MAX_LOCAL_DEAL_RESULTS = 5;
const BASE_INTERVAL_MS = 0.5 * 60_000;
const MAX_BACKOFF_MS = 30 * 60_000;
const BACKOFF_FACTOR = 2;
const JITTER_PCT = 0.2;
const REFRESH_DEBOUNCE_MS = 500;
// Poll interval used when the tenant exceeds BULK_CAP. The local index is
// unusable in that mode, so re-fetching every 5 min just wastes bandwidth on
// a payload we throw away. An hourly check is enough to notice when uploads
// drop the catalog back under the cap; uploads themselves still trigger an
// immediate refresh through the USER_FILES_UPDATED_EVENT path.
const OVERSIZE_INTERVAL_MS = 60 * 60_000;

const listeners = new Set<() => void>();

let storeState: CatalogIndexStoreState = {
	status: "idle",
	docs: [],
	index: null,
	version: 0,
};
let currentUserKey: string | null = null;
let refreshPromise: Promise<void> | null = null;
// User key the in-flight refreshPromise belongs to. Without this, a logout +
// login during a pending fetch would let the new user piggyback on the old
// user's request, which then no-ops via the fetchVersion guard — leaving the
// new session stuck in `idle` until the next poll tick.
let refreshPromiseUserKey: string | null = null;
let pollTimer: number | null = null;
let uploadRefreshTimer: number | null = null;
let refreshDelayMs = BASE_INTERVAL_MS;
let refreshSequence = 0;
let visibilityListenerBound = false;

const notify = () => {
	for (const listener of listeners) {
		listener();
	}
};

const updateStoreState = (next: Partial<CatalogIndexStoreState>) => {
	storeState = {
		...storeState,
		...next,
		version: storeState.version + 1,
	};
	notify();
};

const clearPollTimer = () => {
	if (pollTimer !== null) {
		window.clearTimeout(pollTimer);
		pollTimer = null;
	}
};

const clearUploadRefreshTimer = () => {
	if (uploadRefreshTimer !== null) {
		window.clearTimeout(uploadRefreshTimer);
		uploadRefreshTimer = null;
	}
};

const jitterDelay = (delayMs: number) => {
	const spread = delayMs * JITTER_PCT;
	const min = delayMs - spread;
	return Math.max(1000, Math.round(min + Math.random() * spread * 2));
};

const schedulePoll = (delayMs: number) => {
	clearPollTimer();
	// Tabs in the background pile up timers that all fire on resume — and
	// re-fetching a 660 KB catalog the moment a user switches back to a
	// stale tab is exactly what we want to avoid. Keep the timer cleared
	// while hidden; the visibilitychange handler triggers a single refresh
	// on resume and reschedules from there.
	if (typeof document !== "undefined" && document.hidden) {
		return;
	}
	pollTimer = window.setTimeout(() => {
		void refreshCatalogIndex("poll");
	}, jitterDelay(delayMs));
};

const handleVisibilityChange = () => {
	if (typeof document === "undefined") {
		return;
	}
	if (document.hidden) {
		clearPollTimer();
		return;
	}
	if (!currentUserKey) {
		return;
	}
	// On resume, refresh immediately if a user is logged in. The refresh
	// itself reschedules the next poll from its `.then`/`.catch`, so we
	// don't double-schedule here.
	void refreshCatalogIndex("poll");
};

const ensureVisibilityListener = () => {
	if (visibilityListenerBound || typeof document === "undefined") {
		return;
	}
	document.addEventListener("visibilitychange", handleVisibilityChange);
	visibilityListenerBound = true;
};

const buildIndex = (docs: CatalogDocument[]): CatalogIndex => {
	const index = new MiniSearch<CatalogDocument>({
		idField: "doc_id",
		fields: ["filename", "entity_name", "deal_name", "document_type"],
		storeFields: [
			"doc_id",
			"filename",
			"document_type",
			"entity_name",
			"period_end_date",
			"deal_name",
			"ref_type",
			"project_id",
		],
		searchOptions: {
			prefix: true,
			fuzzy: 0.2,
			boost: { deal_name: 3, entity_name: 3, filename: 2, document_type: 1 },
		},
		extractField: (doc: CatalogDocument, field: string) => {
			const value = doc[field as keyof CatalogDocument];
			return value == null ? "" : String(value);
		},
		tokenize: (text: string) =>
			text
				.toLowerCase()
				.split(/[\s_\-.,;:!?()/]+/)
				.filter(Boolean),
	});
	index.addAll(docs);
	return index;
};

const rankCatalogResults = (
	results: CatalogDocument[],
	limit: number,
): CatalogDocument[] => {
	const dealResults = results
		.filter((doc) => doc.ref_type === "deal")
		.slice(0, MAX_LOCAL_DEAL_RESULTS);
	const documentResults = results.filter((doc) => doc.ref_type !== "deal");
	return [...dealResults, ...documentResults].slice(0, limit);
};

const resetCatalogIndex = () => {
	clearPollTimer();
	clearUploadRefreshTimer();
	refreshPromise = null;
	refreshPromiseUserKey = null;
	refreshDelayMs = BASE_INTERVAL_MS;
	currentUserKey = null;
	refreshSequence += 1;
	updateStoreState({
		status: "idle",
		docs: [],
		index: null,
	});
};

const refreshCatalogIndex = async (
	reason: "initial" | "poll" | "upload",
): Promise<void> => {
	if (!currentUserKey) {
		resetCatalogIndex();
		return Promise.resolve();
	}
	// Only dedup against an in-flight request that belongs to the *same*
	// user. After a logout+login, the pending fetch is owned by the
	// previous session and will no-op via the fetchVersion guard, so
	// piggybacking on it would leave the new session stuck in `idle`.
	if (refreshPromise && refreshPromiseUserKey === currentUserKey) {
		return refreshPromise;
	}

	const fetchVersion = ++refreshSequence;
	const fetchUserKey = currentUserKey;
	const shouldShowLoading = storeState.status === "idle";
	if (shouldShowLoading) {
		updateStoreState({ status: "loading", docs: [], index: null });
	}

	const promise = apiGet<CatalogDocument[]>(
		`/api/v3/catalog/mention-search?limit=${BULK_CAP}&deal_limit=${BULK_CAP}`,
	)
		.then((docs) => {
			if (fetchVersion !== refreshSequence || currentUserKey !== fetchUserKey) {
				return;
			}
			if (docs.length >= BULK_CAP) {
				// Oversize tenants can't be served by the local index; back off
				// the poll significantly so we're not re-pulling 2000+ rows
				// every 5 minutes for a payload we discard.
				refreshDelayMs = OVERSIZE_INTERVAL_MS;
				updateStoreState({ status: "oversize", docs: [], index: null });
				schedulePoll(refreshDelayMs);
				return;
			}
			refreshDelayMs = BASE_INTERVAL_MS;
			updateStoreState({
				status: "ready",
				docs,
				index: buildIndex(docs),
			});
			schedulePoll(refreshDelayMs);
		})
		.catch((error) => {
			console.warn(`Catalog index refresh failed during ${reason}:`, error);
			if (fetchVersion !== refreshSequence || currentUserKey !== fetchUserKey) {
				return;
			}
			refreshDelayMs = Math.min(
				refreshDelayMs * BACKOFF_FACTOR,
				MAX_BACKOFF_MS,
			);
			if (storeState.status === "idle" || storeState.status === "loading") {
				updateStoreState({ status: "error", docs: [], index: null });
			}
			schedulePoll(refreshDelayMs);
		})
		.finally(() => {
			if (refreshPromise === promise) {
				refreshPromise = null;
				refreshPromiseUserKey = null;
			}
		});

	refreshPromise = promise;
	refreshPromiseUserKey = fetchUserKey;
	return promise;
};

const ensureCatalogIndexStarted = (userKey: string | null) => {
	if (!userKey) {
		if (currentUserKey !== null || storeState.status !== "idle") {
			resetCatalogIndex();
		}
		return;
	}

	if (currentUserKey !== userKey) {
		clearPollTimer();
		clearUploadRefreshTimer();
		// Drop the previous user's in-flight request reference so the new
		// session starts a fresh fetch instead of piggybacking. The actual
		// fetch promise can still resolve, but its mismatched fetchUserKey
		// will cause it to no-op.
		refreshPromise = null;
		refreshPromiseUserKey = null;
		refreshDelayMs = BASE_INTERVAL_MS;
		currentUserKey = userKey;
		refreshSequence += 1;
		updateStoreState({ status: "idle", docs: [], index: null });
	}

	if (storeState.status === "idle" && !refreshPromise) {
		void refreshCatalogIndex("initial");
	}
};

export const searchCatalogIndex = (
	docs: CatalogDocument[],
	index: CatalogIndex,
	query: string,
	limit: number,
): CatalogDocument[] => {
	const normalizedQuery = query.trim();
	if (!normalizedQuery) {
		return rankCatalogResults(docs, limit);
	}

	const rawResults = index.search(normalizedQuery, {
		prefix: true,
		fuzzy: 0.2,
		boost: { deal_name: 3, entity_name: 3, filename: 2, document_type: 1 },
	}) as Array<Record<string, unknown>>;
	const mapped: CatalogDocument[] = rawResults.map((result) => ({
		doc_id: String(result.doc_id ?? ""),
		filename: String(result.filename ?? ""),
		document_type: String(result.document_type ?? ""),
		entity_name:
			typeof result.entity_name === "string" ? result.entity_name : null,
		period_end_date:
			typeof result.period_end_date === "string"
				? result.period_end_date
				: null,
		deal_name: String(result.deal_name ?? ""),
		ref_type: result.ref_type === "deal" ? "deal" : "document",
		project_id:
			typeof result.project_id === "string" ? result.project_id : null,
	}));

	return rankCatalogResults(mapped, limit);
};

export interface UseCatalogIndexReturn extends CatalogIndexStoreState {
	refresh: () => Promise<void>;
	prefetch: () => Promise<void>;
}

const useCatalogIndexSnapshotState = (): CatalogIndexStoreState => {
	const [snapshot, setSnapshot] = useState<CatalogIndexStoreState>(storeState);

	useEffect(() => {
		const handleStoreChange = () => {
			setSnapshot(storeState);
		};
		listeners.add(handleStoreChange);
		handleStoreChange();
		return () => {
			listeners.delete(handleStoreChange);
		};
	}, []);

	return snapshot;
};

const useCatalogIndexController = () => {
	const { isAuthenticated, user } = useAuth();
	const userKey = isAuthenticated ? (user?.id ?? "authenticated") : null;

	useEffect(() => {
		ensureVisibilityListener();
		ensureCatalogIndexStarted(userKey);
	}, [userKey]);

	useEffect(() => {
		if (!isAuthenticated) {
			return;
		}
		const handleFilesUpdated = () => {
			clearUploadRefreshTimer();
			uploadRefreshTimer = window.setTimeout(() => {
				uploadRefreshTimer = null;
				void refreshCatalogIndex("upload");
			}, REFRESH_DEBOUNCE_MS);
		};

		window.addEventListener(USER_FILES_UPDATED_EVENT, handleFilesUpdated);
		return () => {
			window.removeEventListener(USER_FILES_UPDATED_EVENT, handleFilesUpdated);
		};
	}, [isAuthenticated]);
};

export const useCatalogIndex = (): UseCatalogIndexReturn => {
	const snapshot = useCatalogIndexSnapshotState();
	useCatalogIndexController();

	return {
		...snapshot,
		refresh: () => refreshCatalogIndex("poll"),
		prefetch: () => refreshCatalogIndex("initial"),
	};
};

export const useCatalogIndexSnapshot = (): UseCatalogIndexReturn => {
	const snapshot = useCatalogIndexSnapshotState();

	return {
		...snapshot,
		refresh: () => refreshCatalogIndex("poll"),
		prefetch: () => refreshCatalogIndex("initial"),
	};
};

export const __resetCatalogIndexForTests = () => {
	resetCatalogIndex();
};
